import React, { useEffect, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import BulkScrubScheduleBuilder from "./BulkScrubScheduleBuilder";
import styles from "./GoHighLevel.module.css";

interface LocationSettings {
  locationId: string;
  locationName: string;
  autoScrubOnLoad: boolean;
  autoScrubOnContactCreate: boolean;
  createNoteOnContactCreate: boolean;
  pinScrubNote: boolean;
  noteColor: string;
  resultNoteBlockedColor: string;
  resultNoteCleanColor: string;
  rescrubIntervalHours: number;
  rescrubIntervalValue: number;
  rescrubIntervalUnit: string;
  bulkScrubEnabled: boolean;
  bulkScrubSchedule: string;
  bulkScrubTimeZone: string;
  manageTags: boolean;
  enabledDncTags: string[];
  dncScrubLoginId: string;
  dncScrubCampaignId: string;
  agencyDncScrubLoginId: string;
  agencyDncScrubCampaignId: string;
}

interface Props {
  locationId: string;
  onSaved?: () => void;
}

type BooleanKeys<T> = { [K in keyof T]: T[K] extends boolean ? K : never }[keyof T];
type StringKeys<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T];

const RESCRUB_UNITS = ["Hours", "Days", "Months"];

const TAG_CATEGORIES = [
  { key: "Blocked", label: "Blocked (DNC-Blocked)" },
  { key: "Clean", label: "Clean (DNC-Clean)" },
  { key: "Wireless", label: "Wireless (DNC-Wireless)" },
  { key: "Invalid", label: "Invalid (DNC-Invalid)" },
];

const LocationSettingsForm: React.FC<Props> = ({ locationId, onSaved }) => {
  const [settings, setSettings] = useState<LocationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSettings(null);
    (async () => {
      try {
        setSettings(await callApi(`sysadmin/gohighlevel/locations/${locationId}/settings`, HttpMethod.GET));
      } catch {
        toast.error("Failed to load settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, [locationId]);

  const update = <K extends keyof LocationSettings>(key: K, value: LocationSettings[K]) =>
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));

  const toggleCategory = (key: string, checked: boolean) =>
    setSettings((prev) => {
      if (!prev) return prev;
      const tags = prev.enabledDncTags ?? [];
      return { ...prev, enabledDncTags: checked ? [...tags, key] : tags.filter((k) => k !== key) };
    });

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await callApi(`sysadmin/gohighlevel/locations/${locationId}/settings`, HttpMethod.PUT, settings);
      toast.success("Settings saved.");
      onSaved?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-muted">Loading settings…</div>;
  if (!settings) return null;

  const renderToggle = (field: BooleanKeys<LocationSettings>, label: string) => (
    <div className={styles.checkRow}>
      <div className="form-check form-switch m-0">
        <input
          type="checkbox"
          role="switch"
          className="form-check-input"
          checked={settings[field]}
          onChange={(e) => update(field, e.target.checked)}
        />
      </div>
      <span>{label}</span>
    </div>
  );

  const renderColorField = (field: StringKeys<LocationSettings>, label: string) => (
    <div className={styles.formGroup}>
      <label className={styles.formLabel}>{label}</label>
      <input
        type="color"
        className="form-control form-control-color"
        value={settings[field] || "#000000"}
        onChange={(e) => update(field, e.target.value)}
      />
    </div>
  );

  return (
    <div className={styles.form}>
      <div className={styles.sectionTitle}>Scrubbing</div>
      {renderToggle("autoScrubOnLoad", "Auto-scrub contacts on page load")}
      {renderToggle("autoScrubOnContactCreate", "Auto-scrub new contacts")}

      <div className={styles.sectionTitle}>Notes</div>
      {renderToggle("createNoteOnContactCreate", "Create DNC note on new contacts")}
      {renderToggle("pinScrubNote", "Pin the scrub note")}

      <div className={styles.sectionTitle}>Colors</div>
      <div className={styles.colorRow}>
        {renderColorField("noteColor", "Note color")}
        {renderColorField("resultNoteBlockedColor", "Blocked result color")}
        {renderColorField("resultNoteCleanColor", "Clean result color")}
      </div>

      <div className={styles.sectionTitle}>Tags</div>
      {renderToggle("manageTags", "Manage DNC tags on contacts")}
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Apply these status tags</label>
        <div className="d-flex flex-wrap gap-3">
          {TAG_CATEGORIES.map((c) => (
            <label key={c.key} className="form-check-label d-inline-flex align-items-center gap-1">
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={(settings.enabledDncTags ?? []).includes(c.key)}
                disabled={!settings.manageTags}
                onChange={(e) => toggleCategory(c.key, e.target.checked)}
              />
              {c.label}
            </label>
          ))}
        </div>
        <div className={styles.formHint}>
          Each contact gets the tag matching its scrub result. Only these DNC tags are ever changed — the contact's other tags are never touched.
        </div>
      </div>

      <div className={styles.sectionTitle}>Bulk Scrub</div>
      {renderToggle("bulkScrubEnabled", "Bulk scrub enabled")}
      <div className={styles.formGroup}>
        <BulkScrubScheduleBuilder
          schedule={settings.bulkScrubSchedule}
          timezone={settings.bulkScrubTimeZone}
          onChange={(schedule, timezone) =>
            setSettings((prev) => (prev ? { ...prev, bulkScrubSchedule: schedule, bulkScrubTimeZone: timezone } : prev))
          }
        />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Re-scrub interval</label>
        <div className="d-flex gap-2">
          <input
            type="number"
            className="form-control"
            style={{ flex: "0 0 7rem" }}
            value={settings.rescrubIntervalValue}
            onChange={(e) => update("rescrubIntervalValue", Number(e.target.value))}
            min={1}
          />
          <select
            className="form-select"
            value={settings.rescrubIntervalUnit}
            onChange={(e) => update("rescrubIntervalUnit", e.target.value)}
          >
            {RESCRUB_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formHint}>
          A number already scrubbed within this period is reused instead of being scrubbed again. Capped at 31 days.
        </div>
      </div>

      <div className={styles.sectionTitle}>DNC Scrub Credentials</div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Login ID override</label>
        <input
          type="text"
          className="form-control"
          value={settings.dncScrubLoginId}
          onChange={(e) => update("dncScrubLoginId", e.target.value)}
          placeholder={settings.agencyDncScrubLoginId ? `Inherits agency: ${settings.agencyDncScrubLoginId}` : "Inherits from agency"}
        />
        <div className={styles.formHint}>Leave blank to inherit from the agency.</div>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Campaign ID override</label>
        <input
          type="text"
          className="form-control"
          value={settings.dncScrubCampaignId}
          onChange={(e) => update("dncScrubCampaignId", e.target.value)}
          placeholder={settings.agencyDncScrubCampaignId ? `Inherits agency: ${settings.agencyDncScrubCampaignId}` : "Inherits from agency"}
        />
        <div className={styles.formHint}>Leave blank to inherit from the agency.</div>
      </div>

      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
};

export default LocationSettingsForm;
